import { EntityType } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { TransactionVendingClient } from '@frontend/transaction/api';
import { ExtendedVendingTransaction } from '@frontend/transaction/types';

import { MultiEntityButton } from '../../../components/multi-entity-button/multi-entity-button.component';
import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import OverviewHeader from '../../header/overview-header';
import { getVendingTransactionTypeDisplay } from '../vending-utils';
import { REPORT_MODAL_ID, useVendingTransactions } from './transactions.controller';

const ID = 'vending-transactions';

const VendingTransactions = () => {
    const viewProps = useVendingTransactions();
    const { openPlatformDetail } = usePlatformModalHost();

    return (
        <div
            id={ID}
            key={ID}
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader title='Transactions' />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <ReactiveObjectList<ExtendedVendingTransaction>
                    key={viewProps.filterKey}
                    dispatch={viewProps.dispatch}
                    fetch={(arg) => TransactionVendingClient.getVendingTransactions(arg)}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.VENDING } })
                    }
                    activeFilter={{
                        searchKeys: ['product', 'user', 'cost_center'],
                        filterKeys: ['spot_id', 'user_id', 'product_id', 'cost_center_id', 'after', 'before'],
                        ...(viewProps.report && {
                            export: {
                                modalId: REPORT_MODAL_ID,
                                report: viewProps.report,
                                data: viewProps.exportData
                            }
                        }),
                        displayName: new Map([
                            ['after', CommonMessage.FORMS.FROM_TIMESTAMP],
                            ['before', CommonMessage.FORMS.TO_TIMESTAMP]
                        ])
                    }}
                    columns={{
                        columns: ['sku', 'ean', 'product', 'quantity', 'categories', 'creation_timestamp', 'spot', 'user', 'cost_center', 'feature'],
                        headerOverwrite: new Map([
                            ['quantity', CommonMessage.OBJECTS.COMMON_FIELDS.AMOUNT],
                            ['spot', CommonMessage.OBJECTS.SPOT.DEFAULT],
                            ['sku', CommonMessage.OBJECTS.PRODUCT.SKU],
                            ['ean', CommonMessage.OBJECTS.PRODUCT.EAN]
                        ]),
                        customCells: new Map([
                            [
                                'categories',
                                (obj) => {
                                    if (obj.row.original.categories === null || obj.row.original.categories.length === 0) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.categories[0].entity_id}
                                            className={defaultCellClassName}>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.categories[0].entity_id}
                                                    entityType={EntityType.PRODUCT_CATEGORY}
                                                    display={obj.row.original.categories[0].name}
                                                    key={obj.row.original.categories[0].entity_id}
                                                    foreign={obj.row.original.categories[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.categories[0])}
                                                />
                                                {obj.row.original.categories.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.categories.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.PRODUCT_CATEGORY}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'product',
                                (obj) =>
                                    obj.row.original.product ? (
                                        <td
                                            className={defaultCellClassName}
                                            key={obj.row.original.product.entity_id}>
                                            <PlatformObjectBadge
                                                display={obj.row.original.product.name}
                                                entityType={obj.row.original.product.entity_type}
                                                id={obj.row.original.product.entity_id}
                                                key={obj.row.original.product.entity_id}
                                                foreign={obj.row.original.product.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.product)}
                                            />
                                        </td>
                                    ) : (
                                        <td></td>
                                    )
                            ],
                            [
                                'spot',
                                (obj) =>
                                    obj.row.original.spot ? (
                                        <td
                                            className={defaultCellClassName}
                                            key={obj.row.original.spot.entity_id}>
                                            <PlatformObjectBadge
                                                display={obj.row.original.spot.name}
                                                entityType={obj.row.original.spot.entity_type}
                                                id={obj.row.original.spot.entity_id}
                                                key={obj.row.original.spot.entity_id}
                                                foreign={obj.row.original.spot.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            />
                                        </td>
                                    ) : (
                                        <td></td>
                                    )
                            ],
                            [
                                'user',
                                (obj) =>
                                    obj.row.original.user ? (
                                        <td
                                            className={defaultCellClassName}
                                            key={obj.row.original.user.entity_id}>
                                            <PlatformObjectBadge
                                                display={obj.row.original.user.name}
                                                entityType={obj.row.original.user.entity_type}
                                                id={obj.row.original.user.entity_id}
                                                key={obj.row.original.user.entity_id}
                                                foreign={obj.row.original.user.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.user)}
                                            />
                                        </td>
                                    ) : (
                                        <td></td>
                                    )
                            ],
                            [
                                'cost_center',
                                (obj) =>
                                    obj.row.original.cost_center ? (
                                        <td
                                            className={defaultCellClassName}
                                            key={obj.row.original.cost_center.entity_id}>
                                            <PlatformObjectBadge
                                                display={obj.row.original.cost_center.name}
                                                entityType={obj.row.original.cost_center.entity_type}
                                                id={obj.row.original.cost_center.entity_id}
                                                key={obj.row.original.cost_center.entity_id}
                                                foreign={obj.row.original.cost_center.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.cost_center)}
                                            />
                                        </td>
                                    ) : (
                                        <td></td>
                                    )
                            ],
                            [
                                'feature',
                                (obj) => {
                                    return (
                                        <td className={defaultCellClassName}>
                                            <div className='text-foreground-default justify-start text-sm font-medium'>
                                                {getVendingTransactionTypeDisplay(obj.row.original.feature)}
                                            </div>
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                />
            </div>
        </div>
    );
};

export default VendingTransactions;
